/* eslint-disable react/jsx-props-no-spreading */
import { ChangeEvent, FC, useEffect, useRef, useState } from "react";
import { Box, Divider } from "@material-ui/core";
import { FormProvider, useForm } from "react-hook-form";
import { useIntl } from "../../../../../_metronic/i18n/customUseIntl";
import { roiCalculatorStyles } from "../../../../ThemeProvider";
import { StageType } from "../../../../../graphql/types";
import {
  RoiCalculatorFormData,
  RoiCalculatorProps,
  RoiFarmGateInputs,
  RoiStage,
} from "../../../models/RoiCalculator";
import {
  FARM_GATE_FIELD_MAP,
  normalizeStageType,
  RoiFormStageDescriptor,
} from "../../../helpers/roiStageData";
import { validateRoiForm } from "./roiValidation";
import CalculationMethodsModal from "./CalculationMethodsModal";
import RoiFormHeader from "./sections/RoiFormHeader";
import CurrencySection from "./sections/CurrencySection";
import FeedCostsSection from "./sections/FeedCostsSection";
import SolutionCostsSection from "./sections/SolutionCostsSection";
import FarmGateSection from "./sections/FarmGateSection";
import CarbonPriceSection from "./sections/CarbonPriceSection";

const buildDefaultFarmGateInputs = (): RoiFarmGateInputs => ({
  liveWeightPrice: "",
  eggSellingPrice: "",
  spentHenRoosterPrice: "",
  consumptionEggPrice: "",
  spentHen: "",
  dayOldChickPrice: "",
  pigletsSellingPrice: "",
  spentSowSellingPrice: "",
  productType: "",
  weight: "",
  price: "",
});

const buildDefaultStages = (stages: RoiFormStageDescriptor[]): RoiStage[] =>
  stages.map((s) => ({
    stageId: s.stageId ?? "",
    stageName: s.stageName ?? "",
    stageType: (s.stageType as StageType) ?? undefined,
    baselineInputs: { feedCost: "" },
    interventionInputs: { feedCost: "" },
  }));

const numToStr = (n?: number): string => (n === undefined ? "" : String(n));

const mapFarmGatePrices = (
  stageType: string | null,
  prices: Record<string, number>,
): Partial<RoiFarmGateInputs> => {
  const pairs = FARM_GATE_FIELD_MAP[normalizeStageType(stageType) as StageType];

  if (!pairs) {
    return {};
  }

  return pairs.reduce<Partial<RoiFarmGateInputs>>(
    (acc, { outputField, formField }) => {
      acc[formField] = numToStr(prices[outputField]);
      return acc;
    },
    {},
  );
};

const RoiCalculatorForm: FC<RoiCalculatorProps> = ({
  formData,
  baselineName,
  interventionName,
  onCurrencyChange,
  onCancel,
  onSubmit,
  readOnly = false,
  savedFormData = null,
  isSubmitting = false,
}) => {
  const intl = useIntl();
  const roiClasses = roiCalculatorStyles();
  const [activeTab, setActiveTab] = useState(0);
  const [methodsModalOpen, setMethodsModalOpen] = useState(false);

  const currencyOptions = formData.currencyOptions.map((c) => {
    const messageId = `SUSTELL.CURRENCY.${c}`;
    const label = intl.formatMessage({ id: messageId, defaultMessage: c });
    return { text: label, value: c };
  });

  const relevantStages = formData.feedBearingStages;

  const methods = useForm<RoiCalculatorFormData>({
    defaultValues: savedFormData || {
      currency: formData.currency,
      inclusionCostOfSolution: "",
      stages: buildDefaultStages(relevantStages),
      farmGate: {
        baselineInputs: buildDefaultFarmGateInputs(),
        interventionInputs: buildDefaultFarmGateInputs(),
      },
      carbonPricePerTonne: "",
    },
    mode: "onSubmit",
    shouldUnregister: false,
  });

  const { setError, clearErrors, watch, setValue, getValues } = methods;

  const selectedCurrency = watch("currency");
  useEffect(() => {
    if (selectedCurrency) {
      onCurrencyChange(selectedCurrency);
    }
  }, [selectedCurrency, onCurrencyChange]);

  const handleTabChange = (_e: ChangeEvent<unknown>, newValue: number) => {
    setActiveTab(newValue);
  };

  const validateFormData = (data: RoiCalculatorFormData): boolean => {
    clearErrors();
    const fieldRequiredMessage = intl.formatMessage({
      id: "SUSTELL.ROI.VALIDATION.FIELD_REQUIRED",
    });
    const errors = validateRoiForm(data, relevantStages, fieldRequiredMessage, {
      terminalOutputStageType: formData.terminalOutputStageType,
      hasProcessingStage: formData.hasProcessingStage,
      farmGateDisabled: formData.farmGate.disabled,
    });

    errors.forEach((err) => {
      setError(err.field, { type: err.type, message: err.message });
    });

    return errors.length === 0;
  };

  const onFormSave = () => {
    const data = getValues();
    if (validateFormData(data)) {
      onSubmit(data);
    }
  };

  const hasBreedingStage = relevantStages.some(
    (s) => s.stageType === StageType.Breeding,
  );
  const solutionCostsKeys = hasBreedingStage
    ? {
        title: "SUSTELL.ROI.SOLUTION_COSTS.ADDITIONAL.TITLE",
        description: "SUSTELL.ROI.SOLUTION_COSTS.ADDITIONAL.DESCRIPTION",
        label: "SUSTELL.ROI.SOLUTION_COSTS.ADDITIONAL.LABEL",
      }
    : {
        title: "SUSTELL.ROI.SOLUTION_COSTS.INCLUSION.TITLE",
        description: "SUSTELL.ROI.SOLUTION_COSTS.INCLUSION.DESCRIPTION",
        label: "SUSTELL.ROI.SOLUTION_COSTS.INCLUSION.LABEL",
      };
  const feedCostsDescriptionId = hasBreedingStage
    ? "SUSTELL.ROI.FEED_COSTS.DESCRIPTION.BREEDING"
    : "SUSTELL.ROI.FEED_COSTS.DESCRIPTION";

  const prefilledRef = useRef<Record<string, string>>({});
  useEffect(() => {
    const applyPrefill = (path: string, value: string) => {
      const current = getValues(path);
      const lastPrefill = prefilledRef.current[path];

      if (current === undefined || current === "" || current === lastPrefill) {
        setValue(path, value);
        prefilledRef.current[path] = value;
      }
    };

    if (formData.hasProcessingStage) {
      // Processing farm-gate values are display-only in PriceAtFarmGateFields,
      // rendered directly from formData.processingOutputs. No form prefill needed.
      return;
    }

    const applySide = (
      side: "baselineInputs" | "interventionInputs",
      prices: Record<string, number>,
    ) => {
      const mapped = formData.farmGate.disabled
        ? {}
        : mapFarmGatePrices(formData.terminalOutputStageType, prices);

      (Object.keys(mapped) as (keyof RoiFarmGateInputs)[]).forEach((key) => {
        applyPrefill(`farmGate.${side}.${key}`, mapped[key] ?? "");
      });
    };
    applySide("baselineInputs", formData.farmGatePrices.baseline);
    applySide("interventionInputs", formData.farmGatePrices.intervention);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.currency,
    formData.farmGate.disabled,
    formData.hasProcessingStage,
    formData.farmGatePrices,
    formData.terminalOutputStageType,
  ]);

  return (
    <FormProvider {...methods}>
      <CalculationMethodsModal
        open={methodsModalOpen}
        onClose={() => setMethodsModalOpen(false)}
      />
      <Box className={roiClasses.pageContainer} style={{ marginTop: "32px" }}>
        <RoiFormHeader
          baselineName={baselineName}
          interventionName={interventionName}
          readOnly={readOnly}
          isSubmitting={isSubmitting}
          onCancel={onCancel}
          onSave={onFormSave}
          onViewMethods={() => setMethodsModalOpen(true)}
        />

        <Divider className={roiClasses.divider} />

        <CurrencySection options={currencyOptions} readOnly={readOnly} />

        <FeedCostsSection
          stages={relevantStages}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          descriptionId={feedCostsDescriptionId}
          readOnly={readOnly}
        />

        <Divider className={roiClasses.divider} />

        <SolutionCostsSection
          messageIds={solutionCostsKeys}
          readOnly={readOnly}
        />

        <Divider className={roiClasses.divider} />

        <FarmGateSection formData={formData} readOnly={readOnly} />

        <Divider className={roiClasses.divider} />

        <CarbonPriceSection readOnly={readOnly} />
      </Box>
    </FormProvider>
  );
};

export default RoiCalculatorForm;
